import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const fieldText = (theme: { palette: { primaryFontFamilyType?: string } }) =>
  `${theme.palette.primaryFontFamilyType}, sans-serif`;

export const StBarOuter = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  background: theme.palette.white.main,
  padding: '0 0 24px',
}));

export const StBarCard = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1202px',
  background: 'transparent',
  padding: '28px 32px',
  [theme.breakpoints.down('lg')]: {
    padding: '24px 32px',
  },
}));

export const StBarRow = styled('form')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
  alignItems: 'end',
  columnGap: '16px',
  rowGap: '16px',
  width: '100%',
  // Tablet: condition full width, location + button on the next row
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    rowGap: '24px',
    '& > :first-of-type': {
      gridColumn: '1 / -1',
    },
  },
  // Mobile: all three stacked, button stays hug and right-aligned
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    '& > :first-of-type': {
      gridColumn: 'auto',
    },
  },
}));

export const StField = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '6px',
  minWidth: 0,
  width: '100%',
});

export const StLabel = styled(Typography)(({ theme }) => ({
  fontFamily: fieldText(theme),
  fontWeight: 700,
  fontSize: '13px',
  lineHeight: '16px',
  color: '#64748B',
}));

export const StInputShell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: '12px',
  width: '100%',
  height: '51px',
  boxSizing: 'border-box',
  padding: '0 16px',
  background: '#F8FAFC',
  borderRadius: '12px',
  position: 'relative',
  '& svg': {
    color: '#94A3B8',
    width: '20px',
    height: '20px',
    fontSize: '20px',
    display: 'block',
    flexShrink: 0,
  },
  // Autocomplete root is position:relative and was growing past the zip field
  '& .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiTextField-root': {
    position: 'static',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: 'auto',
    margin: 0,
  },
  '& .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
    position: 'static',
    background: 'transparent',
    width: '100%',
  },
  '& .MuiAutocomplete-endAdornment': {
    display: 'none',
  },
  // Hero SearchFormInput sets fieldset borderColor #e9e9e9 and leaves
  // Material to thicken it on focus and turn it red on error
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.neutral3.main || '#e9e9e9',
    borderRadius: '12px',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    display: 'none',
  },
  '&& .MuiAutocomplete-input': {
    opacity: '1 !important',
  },
  '&& input, && .MuiInputBase-input, && .MuiAutocomplete-input, && .MuiInputBase-inputSizeSmall': {
    fontFamily: fieldText(theme),
    fontSize: '16px',
    fontWeight: 400,
    color: theme.palette.text.primary,
  },
  '&& input::placeholder, && .MuiInputBase-input::placeholder, && input::-webkit-input-placeholder':
    {
      fontFamily: fieldText(theme),
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '19px',
      color: '#94A3B8',
      opacity: 1,
    },
  '& .MuiFormHelperText-root': {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    margin: 0,
    lineHeight: '16px',
    fontFamily: fieldText(theme),
  },
}));

export const StSubmitWrap = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexShrink: 0,
  '& .MuiButton-root.Mui-focusVisible, & .MuiButton-root:focus-visible': {
    outline: `2px solid ${theme.palette.common.black}`,
    outlineOffset: '2px',
  },
  '& .MuiButton-root': {
    height: '54px',
    padding: '16px 48px',
    borderRadius: '48px',
    fontSize: '18px',
    fontWeight: 700,
    textTransform: 'none',
    minWidth: '187px',
  },
  [theme.breakpoints.down('lg')]: {
    '& .MuiButton-root': {
      height: 'auto',
      minWidth: 'unset',
      padding: '14px 32px',
      borderRadius: '40px',
      fontSize: '16px',
    },
  },
}));
